/**
 * Pending Files Service
 * 
 * Handles operations for guest converter pending files:
 * - Fetching pending file metadata
 * - Downloading pending files
 * - Claiming files after signup/payment
 * - Local storage management
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import app from "./firebase";

const functions = getFunctions(app);

// Cloud Function endpoint URLs
const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL || 
  "https://us-central1-svgtrace-pro.cloudfunctions.net";

// Expiry time constants (must align with server-side values)
// Server: uploadPendingFile.ts sets 1 hour expiry for pending files
// Server: claimPendingFile extends to 24 hours after claim
export const PENDING_FILE_EXPIRY_MS = 1 * 60 * 60 * 1000; // 1 hour (matches server)
export const CLAIMED_FILE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours (matches server)
export const OAUTH_STATE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes for OAuth redirects

/**
 * Get pending file metadata from Cloud Function
 * @param {string} fileId - The pending file ID
 * @returns {Promise<Object>} File metadata
 */
export async function getPendingFileMetadata(fileId) {
  if (!fileId) {
    throw new Error("fileId is required");
  }

  try {
    const response = await fetch(
      `${FUNCTIONS_BASE_URL}/getPendingFileInfo?fileId=${encodeURIComponent(fileId)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch file metadata");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching pending file metadata:", error);
    throw error;
  }
}

/**
 * Download a pending file and return as base64 data URL
 * @param {string} previewUrl - The signed URL for the file
 * @returns {Promise<string>} Base64 data URL of the image
 */
export async function downloadPendingFileAsBase64(previewUrl) {
  if (!previewUrl) {
    throw new Error("previewUrl is required");
  }

  try {
    const response = await fetch(previewUrl);
    
    if (!response.ok) {
      throw new Error("Failed to download file");
    }

    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error downloading pending file:", error);
    throw error;
  }
}

/**
 * Claim a pending file after user signs up/pays
 * Associates the file with the user's account
 * @param {string} fileId - The pending file ID
 * @returns {Promise<Object>} Claim result with new file path
 */
export async function claimPendingFile(fileId) {
  if (!fileId) {
    throw new Error("fileId is required");
  }

  try {
    const claimPendingFileFunc = httpsCallable(functions, "claimPendingFile");
    const result = await claimPendingFileFunc({ fileId });
    return result.data;
  } catch (error) {
    console.error("Error claiming pending file:", error);
    throw error;
  }
}

/**
 * Store pending file ID in localStorage for retrieval after signup
 * @param {string} fileId - The pending file ID
 * @param {Object} metadata - Optional file metadata to store
 */
export function storePendingFileId(fileId, metadata = null) {
  if (!fileId) return;

  const pendingData = {
    fileId,
    metadata,
    storedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem("pendingFile", JSON.stringify(pendingData));
    console.log("✅ Stored pending file ID:", fileId);
  } catch (error) {
    console.warn("Failed to store pending file ID in localStorage:", error);
  }
}

/**
 * Get stored pending file ID from localStorage
 * @returns {Object|null} Pending file data or null
 */
export function getStoredPendingFile() {
  try {
    const stored = localStorage.getItem("pendingFile");
    if (!stored) return null;

    const data = JSON.parse(stored);
    
    // Check if stored data is still valid (matches server-side expiry)
    const storedTime = new Date(data.storedAt).getTime();
    const now = Date.now();
    
    if (now - storedTime > PENDING_FILE_EXPIRY_MS) {
      // Data is stale (file has expired on server), clear it
      clearPendingFile();
      return null;
    }

    return data;
  } catch (error) {
    console.warn("Failed to read pending file from localStorage:", error);
    return null;
  }
}

/**
 * Clear pending file data from localStorage
 */
export function clearPendingFile() {
  try {
    localStorage.removeItem("pendingFile");
    console.log("🗑️ Cleared pending file from localStorage");
  } catch (error) {
    console.warn("Failed to clear pending file from localStorage:", error);
  }
}

/**
 * Get pending file ID from URL query parameters
 * @returns {string|null} File ID or null
 */
export function getPendingFileIdFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("file") || urlParams.get("pendingFile");
}

/**
 * Check if there's a pending file to process
 * Checks both URL params and localStorage
 * @returns {string|null} File ID or null
 */
export function checkForPendingFile() {
  // First check URL params (takes priority)
  const urlFileId = getPendingFileIdFromURL();
  if (urlFileId) {
    return urlFileId;
  }

  // Then check localStorage
  const storedData = getStoredPendingFile();
  if (storedData?.fileId) {
    return storedData.fileId;
  }

  return null;
}

/**
 * Load and prepare a pending file for the converter
 * @param {string} fileId - The pending file ID
 * @param {string} currentUserId - Optional current user ID to allow loading files claimed by same user
 * @returns {Promise<Object>} Object with image data ready for converter
 */
export async function loadPendingFileForConverter(fileId, currentUserId = null) {
  try {
    // Get file metadata
    const metadata = await getPendingFileMetadata(fileId);
    
    if (metadata.status === "expired") {
      throw new Error("This file has expired. Please upload again.");
    }

    // Allow loading if claimed by same user, otherwise throw error
    if (metadata.status === "claimed") {
      if (currentUserId && metadata.claimedBy === currentUserId) {
        // File claimed by current user, allow loading
        console.log("File already claimed by current user, allowing load");
      } else {
        throw new Error("This file has already been claimed by another user.");
      }
    }

    // Download the image as base64
    const imageData = await downloadPendingFileAsBase64(metadata.previewUrl);

    return {
      fileId,
      fileName: metadata.fileName,
      fileSize: metadata.fileSize,
      mimeType: metadata.mimeType,
      imageData, // Base64 data URL ready for converter
      previewUrl: metadata.previewUrl,
      expiresAt: metadata.expiresAt,
      claimedBy: metadata.claimedBy,
      source: metadata.source || "uploaded", // Include source field (generated vs uploaded)
    };
  } catch (error) {
    console.error("Error loading pending file for converter:", error);
    throw error;
  }
}

export default {
  getPendingFileMetadata,
  downloadPendingFileAsBase64,
  claimPendingFile,
  storePendingFileId,
  getStoredPendingFile,
  clearPendingFile,
  getPendingFileIdFromURL,
  checkForPendingFile,
  loadPendingFileForConverter,
};
